import { prefersReducedMotion } from '@theme/utilities';

/**
 * Shade product-page motion layer. Carries the homepage motion vocabulary
 * (shade-hero / shade-reveal) onto the PDP so the product page reads as the
 * same engineered system, not a templated stop.
 *
 *   1. Terminal decode on the mono index eyebrow above the title (the hero's
 *      signature move, ported). Fixed-length character scramble that settles to
 *      the server-rendered text, aria-hidden during the scramble, restored
 *      verbatim. Kicked on a rAF so the real eyebrow paints first; LCP-safe
 *      (the eyebrow is tiny and never the LCP element). Reduced-motion skips it.
 *
 *   2. Gallery frame counter. A mono "IMG 0X / 0N" readout over the gallery,
 *      the PDP echo of the homepage "FRM 0X / 0N" field counters, updated as the
 *      active slide changes (swipe or thumbnail). Informational, so it is built
 *      for everyone; only its fade transition is motion-gated in CSS. Flags the
 *      active slide via [data-shade-active].
 *
 * The buy column itself is intentionally NOT entrance-animated: hiding then
 * fading a conversion column would flicker on first paint. The below-the-fold
 * sections still ride the shared shade-reveal scroll engine.
 *
 * Self-registering, idempotent, theme-editor safe (re-runs on
 * shopify:section:load), reduced-motion gated. Transform/opacity only, no CLS.
 */
class ShadePdp extends HTMLElement {
  /** @type {IntersectionObserver[]} */
  #observers = [];

  /** @type {{el: EventTarget, type: string, fn: EventListener}[]} */
  #listeners = [];

  /** @type {number | null} rAF that defers the decode until after first paint. */
  #decodeKick = null;

  /** @type {number | null} */
  #decodeFrame = null;

  /** @type {HTMLElement | null} */
  #decodeTarget = null;

  /** @type {string} */
  #decodeOriginalHTML = '';

  /** Monospace-friendly scramble glyph set, matching the hero decode. */
  #glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/_<>[]+*';

  connectedCallback() {
    // Only act on a product page (the native product section is present).
    if (!document.querySelector('.product-information')) return;
    this.#attach();
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    this.#teardown();
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  #onSectionLoad = () => {
    this.#teardown();
    this.#attach();
  };

  #attach() {
    // The frame counter is informational, so build it for everyone; its motion
    // (fade) lives in a no-preference CSS query.
    this.#initGalleryCounter();

    // The decode is the motion layer; reduced-motion users keep static text.
    if (prefersReducedMotion()) return;
    // Defer one frame so the real eyebrow text paints before it scrambles.
    this.#decodeKick = requestAnimationFrame(() => {
      this.#decodeKick = null;
      this.#decodeEyebrow();
    });
  }

  #teardown() {
    for (const obs of this.#observers) obs.disconnect();
    this.#observers = [];
    for (const { el, type, fn } of this.#listeners) el.removeEventListener(type, fn);
    this.#listeners = [];

    if (this.#decodeKick !== null) {
      cancelAnimationFrame(this.#decodeKick);
      this.#decodeKick = null;
    }
    this.#cancelDecode();
    document.querySelector('.shade-gallery-counter')?.remove();
  }

  // -------- Terminal decode on the index eyebrow --------

  #decodeEyebrow() {
    const target = /** @type {HTMLElement | null} */ (document.querySelector('.shade-pdp-eyebrow'));
    if (!target) return;
    // Never scramble a heading (would mutate SEO/LCP text); the eyebrow is a <p>.
    if (/^H[1-6]$/.test(target.tagName)) return;

    const finalText = target.textContent ?? '';
    const length = finalText.length;
    if (length === 0) return;

    this.#decodeTarget = target;
    this.#decodeOriginalHTML = target.innerHTML;
    target.setAttribute('aria-hidden', 'true');

    const totalFrames = Math.min(length + 6, 26);
    let frame = 0;

    const tick = () => {
      const settled = Math.floor((frame / totalFrames) * length);
      let out = '';
      for (let i = 0; i < length; i += 1) {
        const realChar = finalText[i];
        // Keep spaces and the literal separators so the shape reads true.
        if (i < settled || realChar === ' ' || realChar === '/' || realChar === '_') {
          out += realChar;
        } else {
          out += this.#glyphs[(Math.random() * this.#glyphs.length) | 0];
        }
      }
      target.textContent = out;
      frame += 1;

      if (frame <= totalFrames) {
        this.#decodeFrame = requestAnimationFrame(tick);
      } else {
        target.innerHTML = this.#decodeOriginalHTML;
        target.removeAttribute('aria-hidden');
        this.#decodeTarget = null;
        this.#decodeFrame = null;
      }
    };

    this.#decodeFrame = requestAnimationFrame(tick);
  }

  #cancelDecode() {
    if (this.#decodeFrame !== null) {
      cancelAnimationFrame(this.#decodeFrame);
      this.#decodeFrame = null;
    }
    if (this.#decodeTarget) {
      this.#decodeTarget.innerHTML = this.#decodeOriginalHTML;
      this.#decodeTarget.removeAttribute('aria-hidden');
      this.#decodeTarget = null;
    }
  }

  // -------- Gallery frame counter + active-frame flag --------

  #initGalleryCounter() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    const slides = /** @type {HTMLElement[]} */ (Array.from(gallery.querySelectorAll('slideshow-slide')));
    if (slides.length < 2) return;
    if (gallery.querySelector('.shade-gallery-counter')) return;
    const total = slides.length;

    const counter = document.createElement('div');
    counter.className = 'shade-gallery-counter';
    counter.setAttribute('aria-hidden', 'true');
    gallery.appendChild(counter);

    const pad = (n) => String(n).padStart(2, '0');
    const render = (idx) => {
      counter.textContent = `IMG ${pad(idx + 1)} / ${pad(total)}`;
      slides.forEach((s, i) => {
        s.dataset.shadeActive = i === idx ? 'true' : 'false';
      });
    };
    render(0);

    // Active = the slide most visible inside the gallery scroller. Covers swipe
    // and thumbnail-driven scroll on the carousel. Geometry/observer based, same
    // approach as the homepage gallery scrub.
    const scroller = gallery.querySelector('slideshow-slides') || gallery;
    const ratios = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0);
        let best = -1;
        let bestIdx = 0;
        slides.forEach((s, i) => {
          const r = ratios.get(s) || 0;
          if (r > best) {
            best = r;
            bestIdx = i;
          }
        });
        if (best > 0) render(bestIdx);
      },
      { root: scroller, threshold: [0.25, 0.5, 0.75, 1] }
    );
    slides.forEach((s) => io.observe(s));
    this.#observers.push(io);

    // Belt and braces for fade-mode galleries where the scroller never moves:
    // a thumbnail click maps to its index directly.
    const thumbs = gallery.querySelector('.slideshow-controls__thumbnails');
    if (thumbs) {
      const onClick = (event) => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('.slideshow-controls__thumbnail');
        if (!btn) return;
        const all = Array.from(thumbs.querySelectorAll('.slideshow-controls__thumbnail'));
        const idx = all.indexOf(btn);
        if (idx >= 0) render(idx);
      };
      thumbs.addEventListener('click', onClick);
      this.#listeners.push({ el: thumbs, type: 'click', fn: onClick });
    }
  }
}

if (!customElements.get('shade-pdp')) {
  customElements.define('shade-pdp', ShadePdp);
}

// Self-instantiate one instance (this module only loads on product templates).
if (!document.querySelector('shade-pdp')) {
  document.body.appendChild(document.createElement('shade-pdp'));
}
